import type { Placement } from '@floating-ui/react-dom';
import type { CSSProperties } from 'react';
import { FloatingPortal } from '@floating-ui/react';
import { autoUpdate, flip, offset, useFloating } from '@floating-ui/react-dom';
import { groupBy } from 'lodash';
import { useEffect, useState } from 'react';

import { ButtonIcon, ButtonText } from '../Button';
import { cn } from '../cn';
import { buttonHeightAndBorder, getBackgroundStyle } from '../common';
import { ColorOption } from '../common.type';
import { Arrows, General } from '../StrokeIcons';
import { InputProps } from './Input';
import SelectDropdownContainer from './SelectDropdownContainer';

export type SelectColorProps = Omit<InputProps, 'rIcon' | 'onChange'> & {
  placement?: Placement;
  options?: ColorOption[];
  value?: string;
  onChange?: (value: ColorOption) => void;
};

export default function SelectColor({
  size = 'base',
  title,
  error,
  value,
  className,
  options,
  placement,
  onChange,
  titleClassName,
  ref,
  ...props
}: SelectColorProps) {
  const _valueOption = options?.find((o) => o.value === value || o.value === props.defaultValue);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const selectedOptionValue = _valueOption?.value;

  const groupedOptions = groupBy(options, (o) => o.colorValue.type);

  const { x, y, strategy, refs } = useFloating<HTMLLabelElement>({
    placement,
    middleware: [offset(4), flip()],
    whileElementsMounted: autoUpdate,
  });

  const selectOption = (el: ColorOption) => {
    setIsFocused(false);
    if (onChange) {
      onChange(el);
    }
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (
      refs.floating.current &&
      !refs.floating.current.contains(e.target as Node) &&
      refs.reference.current &&
      !refs.reference.current.contains(e.target as Node)
    ) {
      setIsFocused(false);
    }
  };

  // Add click outside listener
  useEffect(() => {
    if (isFocused) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return () => {};
  }, [isFocused]);

  const selectedOption = options?.find((o) => o.value === selectedOptionValue);

  const solidColors = groupedOptions['solid'];
  const gradientColors = groupedOptions['gradient'];

  return (
    <div
      className={cn(
        'inline-flex min-w-28 flex-col text-slate-600 dark:text-slate-100',
        {
          'gap-1': size === 'base',
          'min-w-22 gap-0.5': size === 'sm',
          'min-w-18 gap-0': size === 'xs',
        },
        className,
      )}>
      <label
        ref={refs.setReference}
        onClick={() => setIsFocused((f) => !f)}
        className={cn(
          'inline-flex overflow-hidden font-sans transition-colors cursor-pointer',
          'bg-white focus-within:bg-apricot-white-50',
          'dark:border-charade-700 dark:bg-charade-950 dark:focus-within:bg-charade-800',
          {
            'border border-slate-200': true,
            'border-fair-pink-300 dark:border-fair-pink-900': error,
            'bg-fair-pink-100 text-fair-pink-600 dark:bg-fair-pink-950 dark:text-fair-pink-700 dark:focus-within:bg-fair-pink-900':
              error,
          },
          buttonHeightAndBorder(size),
        )}>
        <div className="flex items-center px-2">
          <div
            className="h-4 w-4 rounded-full border border-slate-200"
            style={selectedOption ? getBackgroundStyle(selectedOption.colorValue) : undefined}
          />
        </div>

        <ButtonText size={size} className="justify-start">
          {title}
        </ButtonText>
        <ButtonText size={size} className="justify-end opacity-60">
          {_valueOption?.label || ''}
        </ButtonText>
        <ButtonIcon size={size}>
          <Arrows.ChevronSelectorVertical />
        </ButtonIcon>
      </label>

      <FloatingPortal>
        <SelectDropdownContainer
          x={x}
          y={y}
          setFloating={refs.setFloating}
          strategy={strategy}
          isOpen={isFocused}
          reference={refs.reference}
          size={size}>
          {(solidColors?.length ?? 0) > 0 && <div className="pt-1 px-2 text-xs">Solid</div>}
          <div className="flex flex-wrap gap-2 p-2">
            {solidColors?.map((option) => (
              <ColorPickerItem
                key={option.value}
                color={option}
                isSelected={selectedOptionValue === option.value}
                onClick={() => selectOption(option)}
              />
            ))}
          </div>
          {(gradientColors?.length ?? 0) > 0 && <div className="pt-1 px-2 text-xs">Gradient</div>}
          <div className="flex flex-wrap gap-2 p-2">
            {gradientColors?.map((option) => (
              <ColorPickerItem
                key={option.value}
                color={option}
                isSelected={selectedOptionValue === option.value}
                onClick={() => selectOption(option)}
              />
            ))}
          </div>
        </SelectDropdownContainer>
      </FloatingPortal>
    </div>
  );
}

function ColorPickerItem({
  color,
  isSelected,
  onClick,
}: {
  color: ColorOption;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={cn('size-6 rounded-full transition-all duration-300 cursor-pointer flex items-center justify-center', {
        'opacity-100': isSelected,
        'opacity-70 hover:opacity-100': !isSelected,
      })}
      style={
        {
          background:
            color.colorValue?.type === 'gradient'
              ? `linear-gradient(${color.colorValue.angle ?? 90}deg, ${color.colorValue.stops.map((stop) => `${stop.color} ${stop.position}%`).join(', ')})`
              : color.colorValue?.color,
          ['--tw-ring-color']:
            color.colorValue?.type === 'gradient' ? color.colorValue.stops[0]?.color : color.colorValue?.color,
        } as CSSProperties
      }>
      {isSelected && <General.Check className="size-4 text-white" />}
    </div>
  );
}
